import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from "@mui/material";
import { useAddVolunteerMutation, useUpdateVolunteerMutation } from "../api/volunteersApi";
import { Volunteer, VolunteerFormData } from "../types/volunteer";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  volunteer?: Volunteer | null;
}

const AddEditVolunteer: React.FC<Props> = ({ open, setModalOpen, onSuccess, volunteer }) => {
  const [formData, setFormData] = useState<VolunteerFormData>({
    name: "",
    email: "",
    phone: "",
    profile_image: null,
    existingImage: undefined,
    status: "active",
    availability: "1",
    skills: ""
  });

  const [addVolunteer] = useAddVolunteerMutation();
  const [updateVolunteer] = useUpdateVolunteerMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (volunteer) {
      setFormData({
        name: volunteer.name || "",
        email: volunteer.email || "",
        phone: volunteer.phone || "",
        profile_image: null,
        existingImage: volunteer.profile_image_url || undefined,
        status: volunteer.status || "active",
        availability: volunteer.availability || "1",
        skills: volunteer.skills || ""
      });
    } else {
      setFormData({
        name: "",
        email: "",
        phone: "",
        profile_image: null,
        existingImage: undefined,
        status: "active",
        availability: "1",
        skills: ""
      });
    }
    setSubmissionError(null);
  }, [volunteer]);

  const handleInputChange = (field: string, value: any) => {
    // Special handling for profile_image field to prevent empty objects
    if (field === 'profile_image' && (!value || !(value instanceof File))) {
      value = null;
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (files: FileList | null) => {
    if (files && files.length > 0) {
      handleInputChange('profile_image', files[0]);
    } else {
      handleInputChange('profile_image', null);
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!formData.name.trim()) {
      setSubmissionError("Volunteer name is required");
      return;
    }

    if (!formData.email.trim()) {
      setSubmissionError("Email is required");
      return;
    }

    if (!formData.phone.trim()) {
      setSubmissionError("Phone number is required");
      return;
    }

    // For new volunteers, require a profile image
    if (!volunteer && (!formData.profile_image || !(formData.profile_image instanceof File))) {
      setSubmissionError("Please select a profile image for the volunteer");
      return;
    }

    setIsSaving(true);
    setSubmissionError(null);

    try {
      // Final validation - ensure profile_image is either null or a valid File
      if (formData.profile_image && !(formData.profile_image instanceof File)) {
        console.error('Invalid profile image detected:', formData.profile_image);
        setSubmissionError("Please select a valid profile image file");
        setIsSaving(false);
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('status', formData.status);
      formDataToSend.append('availability', formData.availability);
      formDataToSend.append('skills', formData.skills);

      // Handle profile_image field - only append if we have a valid File
      if (formData.profile_image && formData.profile_image instanceof File) {
        formDataToSend.append('profile_image', formData.profile_image);
        console.log('Appending profile image file:', formData.profile_image.name, 'Type:', formData.profile_image.type);
      } else if (volunteer && formData.existingImage) {
        // For updates, if no new image is selected, don't send image field
        // The existing image should be preserved
        console.log('Update mode: No new profile image selected, keeping existing image');
      } else if (!volunteer) {
        // For new volunteers, profile image is required
        setSubmissionError("Please select a profile image for the volunteer");
        setIsSaving(false);
        return;
      }

      // Debug: Log form data being sent
      console.log('=== VOLUNTEER FORM DEBUG ===');
      console.log('formData.profile_image:', formData.profile_image);
      console.log('formData.profile_image type:', typeof formData.profile_image);
      console.log('formData.profile_image instanceof File:', formData.profile_image instanceof File);
      console.log('Is creating new volunteer:', !volunteer);
      console.log('Has existing image:', !!formData.existingImage);
      console.log('FormData entries:');
      Array.from(formDataToSend.entries()).forEach(([key, value]) => {
        console.log(`  ${key}:`, value, `(type: ${typeof value})`);
        if (value instanceof File) {
          console.log(`    File details - name: ${value.name}, size: ${value.size}, type: ${value.type}`);
        }
      });
      console.log('=== END DEBUG ===');

      if (volunteer) {
        await updateVolunteer({
          id: volunteer.id.toString(),
          body: formDataToSend
        }).unwrap();
      } else {
        await addVolunteer({
          body: formDataToSend
        }).unwrap();
      }

      setModalOpen(false);
      // Reset form data after successful submission
      setFormData({
        name: "",
        email: "",
        phone: "",
        profile_image: null,
        existingImage: undefined,
        status: "active",
        availability: "1",
        skills: ""
      });
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onSuccess?.();
    } catch (error: any) {
      console.error('Error saving volunteer:', error);
      const errorMessage = error?.data?.message || 
                          error?.data?.error || 
                          error?.message || 
                          "An error occurred while saving the volunteer";
      setSubmissionError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [formData, volunteer, addVolunteer, updateVolunteer, setModalOpen, onSuccess]);

  const handleClose = () => {
    setModalOpen(false);
    setSubmissionError(null);
    // Reset form data when closing
    if (!volunteer) {
      setFormData({
        name: "",
        email: "",
        phone: "",
        profile_image: null,
        existingImage: undefined,
        status: "active",
        availability: "1",
        skills: ""
      });
    }
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {volunteer ? "Edit Volunteer" : "Add New Volunteer"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {submissionError && (
            <Alert severity="error">{submissionError}</Alert>
          )}

          <TextField
            label="Full Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            fullWidth
            required
          />

          <TextField
            label="Phone Number"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            fullWidth
            required
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.availability === "1"}
                  onChange={(e) => handleInputChange('availability', e.target.checked ? "1" : "0")}
                />
              }
              label="Available"
            />
          </Box>

          <TextField
            label="Skills"
            value={formData.skills}
            onChange={(e) => handleInputChange('skills', e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="e.g., Software engineer, Marketing, Event planning, etc."
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Profile Image {!volunteer && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <input
              ref={fileInputRef}
              key={volunteer ? `edit-${volunteer.id}` : 'new'}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e.target.files)}
              style={{ marginBottom: '8px' }}
              required={!volunteer}
            />
            {formData.existingImage && !formData.profile_image && (
              <Box sx={{ mt: 1 }}>
                <img
                  src={formData.existingImage}
                  alt="Current profile image"
                  style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4 }}
                />
              </Box>
            )}
            {formData.profile_image && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="success.main">
                  New image selected: {formData.profile_image.name}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? "Saving..." : (volunteer ? "Update" : "Create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditVolunteer;
